import { useState, useRef } from 'react'
import {
  PhotoIcon,
  CloudArrowUpIcon,
  XMarkIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline'
import { useAuth0 } from '@auth0/auth0-react'
import { galleryApi } from '../services/galleryApi'
import RetryImage from './RetryImage'
import toast from 'react-hot-toast'

interface PhotoUploadProps {
  currentPhotoUrl?: string
  onPhotoUploaded: (photoUrl: string) => void
  onPhotoRemoved: () => void
  className?: string
}

export default function PhotoUpload({
  currentPhotoUrl,
  onPhotoUploaded,
  onPhotoRemoved,
  className = ""
}: PhotoUploadProps) {
  const { getAccessTokenSilently } = useAuth0()
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [dragActive, setDragActive] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0])
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault()
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0])
    }
  }

  const handleFileUpload = async (file: File) => {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file')
      return
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB')
      return
    }

    try {
      setIsUploading(true)
      setUploadProgress(0)

      // Get upload URL
      const { uploadUrl, mediaUrl } = await galleryApi.getUploadUrl(
        file.name,
        file.type,
        getAccessTokenSilently
      )

      console.log('Upload URLs received:', { uploadUrl, mediaUrl })

      // Upload file with progress tracking
      await galleryApi.uploadFile(file, uploadUrl, (progress) => {
        setUploadProgress(progress)
      })

      console.log('File uploaded successfully, final mediaUrl:', mediaUrl)

      // Wait a moment for CloudFront propagation and test the URL
      await new Promise(resolve => setTimeout(resolve, 2000))
      
      try {
        const response = await fetch(mediaUrl, { method: 'HEAD' })
        if (!response.ok) {
          console.warn('Image not immediately available via CloudFront, but upload was successful')
          toast.success('Photo uploaded successfully (may take a moment to appear)')
        } else {
          console.log('Image confirmed available via CloudFront')
          toast.success('Photo uploaded successfully')
        }
      } catch (fetchError) {
        console.warn('Could not verify image availability, but upload was successful:', fetchError)
        toast.success('Photo uploaded successfully (may take a moment to appear)')
      }

      // Notify parent component
      onPhotoUploaded(mediaUrl)
    } catch (error: any) {
      console.error('Upload error:', error)
      toast.error(error.message || 'Failed to upload photo')
    } finally {
      setIsUploading(false)
      setUploadProgress(0)
    }
  }

  const handleRemovePhoto = () => {
    onPhotoRemoved()
  }

  const handleClick = () => {
    fileInputRef.current?.click()
  }

  return (
    <div className={`space-y-3 ${className}`}>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        Photo
      </label>
      
      {currentPhotoUrl ? (
        // Show current photo with option to replace or remove
        <div className="relative">
          <div className="relative w-32 h-32 rounded-lg overflow-hidden border-2 border-gray-200 dark:border-gray-600">
            <RetryImage
              src={currentPhotoUrl}
              alt="Memorial photo"
              className="w-full h-full object-cover"
              maxRetries={5}
              retryDelay={3000}
            />
          </div>
          <div className="absolute top-2 right-2 flex space-x-1">
            <button
              type="button"
              onClick={handleClick}
              className="p-1 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors"
              title="Replace photo"
            >
              <PhotoIcon className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={handleRemovePhoto}
              className="p-1 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors"
              title="Remove photo"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      ) : (
        // Show upload area
        <div
          className={`relative border-2 border-dashed rounded-lg p-6 text-center hover:border-rb-gold transition-colors cursor-pointer ${
            dragActive
              ? 'border-rb-gold bg-yellow-50 dark:bg-yellow-900/20'
              : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
          } ${isUploading ? 'pointer-events-none' : ''}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={handleClick}
        >
          {isUploading ? (
            <div className="space-y-3">
              <CloudArrowUpIcon className="w-12 h-12 text-rb-gold mx-auto animate-pulse" />
              <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Uploading... {Math.round(uploadProgress)}%
                </p>
                <div className="mt-2 w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div
                    className="bg-rb-gold h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <PhotoIcon className="w-12 h-12 text-gray-400 mx-auto" />
              <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Click to upload or drag and drop
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  PNG, JPG, JPEG up to 10MB
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleChange}
        className="hidden"
      />
    </div>
  )
}
